/**
 * TopicCard Component
 *
 * Displays a topic card with vertical layout:
 * - Top row: title, metadata, style selector (left) + status badges (right)
 * - Divider
 * - Action bar: primary actions (left) + secondary actions + delete (right)
 *
 * Supports both dark and light themes using CSS variables.
 */

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'convex/react';
import {
  Trash2,
  Sparkles,
  Loader2,
  Pencil,
  ImageIcon,
  Calendar,
  Palette,
  Film,
  FileText,
  ChevronDown,
} from 'lucide-react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: convex generated
import { ITopicCardProps } from '../../@types/topics'; // DEEP_IMPORT_JUSTIFIED: types
import { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: convex generated
import { cn } from '@/lib/utils';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { PublishButtonMemo } from '../publishing/PublishButton';
import { DeleteConfirmDialog } from './DeleteConfirmDialog';
import { WritingStyleSelectorMemo } from './WritingStyleSelector';
import { DropdownGenerateMemo } from './DropdownGenerate';

const TopicCard = ({ topic, onDelete, onGenerate, onEdit }: ITopicCardProps) => {
  const navigate = useNavigate();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedStyleId, setSelectedStyleId] = useState<Id<'writingStyles'> | undefined>(
    topic.writingStyleId
  );

  const post = useQuery(api.posts.getLatestPostByTopic, { topicId: topic._id });
  const reel = useQuery(api.reels.getLatestReelByTopic, { topicId: topic._id });
  const latestImage = useQuery(
    api.ai.queries.getLatestImageByPost,
    post?._id ? { postId: post._id } : 'skip'
  );
  const research = useQuery(api.research.getLatestResearch, {
    entityId: topic._id,
    entityType: 'topic',
  });
  const isPublished = useQuery(
    api.posts.hasPostBeenPublished,
    post?._id ? { postId: post._id } : 'skip'
  );

  const hasCompletedResearch = research !== null && research?.status === 'completed';
  const isResearchPending = research !== null && research?.status === 'pending';
  const hasGeneratedPost = post !== null && post !== undefined && post.status === 'completed';
  const isPostPending = post !== null && post?.status === 'pending';
  const isImageGenerating =
    latestImage?.status === 'generating' || latestImage?.status === 'pending';
  const hasGeneratedReel = reel != null && reel.status === 'completed';
  const isReelGenerating =
    reel != null &&
    (reel.status === 'pending' || reel.status === 'researching' || reel.status === 'generating');

  const cardState = (() => {
    if (isPublished) return 'published';
    if (isImageGenerating) return 'generating_image';
    if (latestImage?.status === 'completed') return 'image_generated';
    if (isPostPending) return 'generating_post';
    if (isResearchPending) return 'researching';
    if (hasGeneratedPost) return 'post_generated';
    if (isReelGenerating) return 'generating_reel';
    if (hasGeneratedReel) return 'reel_generated';
    if (hasCompletedResearch) return 'researched';
    return 'not_started';
  })();

  const postId = post?._id;
  const reelId = reel?._id;

  const handleDeleteClick = useCallback(() => setShowDeleteDialog(true), []);
  const handleEditClick = useCallback(() => {
    if (onEdit) onEdit(topic);
  }, [topic, onEdit]);
  const handleConfirmDelete = useCallback(() => {
    onDelete(topic._id);
    setShowDeleteDialog(false);
  }, [topic._id, onDelete]);
  const handleCancelDelete = useCallback(() => setShowDeleteDialog(false), []);
  const handleGenerateClick = useCallback(() => {
    if (onGenerate) onGenerate(topic._id);
  }, [topic._id, onGenerate]);
  const handleViewPost = useCallback(() => {
    if (postId) navigate(`/post/${postId}`);
  }, [navigate, postId]);
  const handleGenerateImage = useCallback(() => {
    if (postId) navigate(`/post/${postId}#image`);
  }, [navigate, postId]);
  const handleRegeneratePost = useCallback(() => {
    navigate(`/post-generation/${topic._id}?mode=regenerate`);
  }, [navigate, topic._id]);
  const handleGenerateReel = useCallback(() => {
    navigate(`/reel-generation/${topic._id}`);
  }, [navigate, topic._id]);
  const handleViewReel = useCallback(() => {
    if (reelId) navigate(`/reel/${reelId}`);
  }, [navigate, reelId]);
  const handleWritingStyleChange = useCallback(
    (styleId: Id<'writingStyles'> | undefined) => setSelectedStyleId(styleId),
    []
  );

  const formattedDate = new Date(topic.generatedAt).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  return (
    <>
      <div className="bg-topic-card border border-topic-card-border rounded-[14px] shadow-[0_2px_8px_rgba(0,0,0,0.02)]">
        {/* Top Row: Content + Badges */}
        <div className="flex justify-between gap-4 px-6 pt-[18px]">
          <div className="flex-1 min-w-0 space-y-2">
            <h3 className="font-semibold text-[15px] text-foreground leading-tight">
              {topic.name}
            </h3>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-1.5">
                <Calendar className="h-[13px] w-[13px] text-topic-meta" />
                <span className="text-xs text-topic-meta">{formattedDate}</span>
              </div>
              {topic.businessProfileSnapshot?.businessCategory && (
                <span className="text-[11px] font-medium px-2.5 py-[3px] rounded-md bg-category-badge-bg text-category-badge">
                  {topic.businessProfileSnapshot.businessCategory}
                </span>
              )}
              {topic.source === 'manual' && (
                <span className="text-xs px-2.5 py-1 rounded-md border border-source-badge-border text-source-badge-fg">
                  Manual
                </span>
              )}
              {topic.source === 'ai' && (
                <span className="text-xs px-2.5 py-1 rounded-md border border-source-badge-border text-source-badge-fg">
                  AI
                </span>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <Palette className="h-[13px] w-[13px] text-topic-meta" />
              <WritingStyleSelectorMemo
                topicId={topic._id}
                value={selectedStyleId}
                onChange={handleWritingStyleChange}
              />
            </div>
          </div>
          <div className="flex items-start gap-1.5 flex-shrink-0">
            <TopicBadges cardState={cardState} hasGeneratedReel={hasGeneratedReel} />
          </div>
        </div>

        {/* Divider */}
        <div className="mx-6 my-3.5 h-px bg-topic-divider" />

        {/* Action Bar */}
        <div className="flex items-center justify-between px-6 pb-[18px]">
          <TopicActionBarLeft
            cardState={cardState}
            onGenerate={handleGenerateClick}
            onGenerateReel={handleGenerateReel}
            onViewPost={handleViewPost}
            onViewReel={handleViewReel}
            onGenerateImage={handleGenerateImage}
            onRegeneratePost={handleRegeneratePost}
            postId={postId}
            hasGeneratedReel={hasGeneratedReel}
          />
          <TopicActionBarRight
            cardState={cardState}
            onGenerateReel={handleGenerateReel}
            onViewReel={handleViewReel}
            onGenerate={handleGenerateClick}
            onRegeneratePost={handleRegeneratePost}
            hasGeneratedReel={hasGeneratedReel}
            isReelGenerating={isReelGenerating}
            onDelete={handleDeleteClick}
            onEdit={onEdit ? handleEditClick : undefined}
            topicSource={topic.source ?? 'ai'}
          />
        </div>
      </div>

      <DeleteConfirmDialog
        isOpen={showDeleteDialog}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        topicName={topic.name}
      />
    </>
  );
};

/** Status badges (supports showing multiple) */
function TopicBadges({
  cardState,
  hasGeneratedReel,
}: {
  cardState: string;
  hasGeneratedReel: boolean;
}) {
  const badgeBase =
    'text-[10px] font-semibold tracking-wider px-3.5 py-1.5 rounded-full border whitespace-nowrap';
  const publishedClass =
    'bg-status-published-bg text-status-published border-status-published-border';
  const notGeneratedClass =
    'bg-status-not-generated-bg text-status-not-generated border-status-not-generated-border';
  const reelClass = 'bg-status-reel-bg text-status-reel border-status-reel-border';
  const activeClass = 'bg-status-active-bg text-status-active border-status-active-border';

  const stateConfig: Record<string, { text: string; className: string }> = {
    published: { text: 'PUBLISHED', className: publishedClass },
    not_started: { text: 'NOT GENERATED', className: notGeneratedClass },
    reel_generated: { text: 'REEL READY', className: reelClass },
    researching: { text: 'RESEARCHING...', className: activeClass },
    researched: { text: 'RESEARCHED', className: activeClass },
    generating_post: { text: 'GENERATING...', className: activeClass },
    post_generated: { text: 'TEXT READY', className: activeClass },
    generating_image: { text: 'IMAGE GEN...', className: activeClass },
    image_generated: { text: 'READY', className: activeClass },
    generating_reel: { text: 'GENERATING REEL...', className: activeClass },
  };

  const primary = stateConfig[cardState] ?? stateConfig.not_started;
  const showReelBadge =
    hasGeneratedReel &&
    ['published', 'image_generated', 'post_generated', 'generating_image'].includes(cardState);

  return (
    <>
      <span className={cn(badgeBase, primary.className)}>{primary.text}</span>
      {showReelBadge && <span className={cn(badgeBase, reelClass)}>REEL READY</span>}
    </>
  );
}

/** Left side of action bar: primary action buttons */
function TopicActionBarLeft({
  cardState,
  onGenerate,
  onGenerateReel,
  onViewPost,
  onViewReel,
  onGenerateImage,
  onRegeneratePost,
  postId,
  hasGeneratedReel,
}: {
  cardState: string;
  onGenerate: () => void;
  onGenerateReel: () => void;
  onViewPost: () => void;
  onViewReel: () => void;
  onGenerateImage: () => void;
  onRegeneratePost: () => void;
  postId?: Id<'posts'>;
  hasGeneratedReel: boolean;
}) {
  const outlineBtn =
    'border border-icon-btn-border text-topic-action-text hover:text-foreground text-xs font-medium px-3.5 py-[7px] rounded-lg gap-1.5 inline-flex items-center transition-colors';

  if (cardState === 'not_started' || cardState === 'researched') {
    return (
      <div className="flex items-center gap-2">
        <DropdownGenerateMemo onGeneratePost={onGenerate} onGenerateReel={onGenerateReel} />
      </div>
    );
  }

  if (cardState === 'researching') {
    return (
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" disabled className="gap-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Researching...
        </Button>
      </div>
    );
  }

  if (cardState === 'generating_post') {
    return (
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" disabled className="gap-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Generating...
        </Button>
      </div>
    );
  }

  if (cardState === 'generating_reel') {
    return (
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" disabled className="gap-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Generating Reel...
        </Button>
      </div>
    );
  }

  if (cardState === 'reel_generated') {
    return (
      <div className="flex items-center gap-2">
        <Button onClick={onViewReel} size="sm" className="gap-1.5">
          <Film className="h-3.5 w-3.5" />
          View Reel
        </Button>
      </div>
    );
  }

  if (cardState === 'post_generated') {
    return (
      <div className="flex items-center gap-2">
        <Button onClick={onViewPost} size="sm" className="gap-1.5">
          <FileText className="h-3.5 w-3.5" />
          View Post
        </Button>
        <button onClick={onGenerateImage} className={outlineBtn}>
          <ImageIcon className="h-3.5 w-3.5" />
          Image
        </button>
        <RegenDropdown
          onRegenPost={onRegeneratePost}
          onRegenReel={onGenerateReel}
          className={outlineBtn}
        />
      </div>
    );
  }

  if (cardState === 'generating_image') {
    return (
      <div className="flex items-center gap-2">
        <Button onClick={onViewPost} size="sm" className="gap-1.5">
          <FileText className="h-3.5 w-3.5" />
          View Post
        </Button>
        <Button variant="outline" size="sm" disabled className="gap-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Image...
        </Button>
      </div>
    );
  }

  if (cardState === 'image_generated' && postId) {
    return (
      <div className="flex items-center gap-2">
        <Button onClick={onViewPost} size="sm" className="gap-1.5">
          <FileText className="h-3.5 w-3.5" />
          View Post
        </Button>
        <PublishButtonMemo postId={postId} className="h-8 text-xs" />
        <RegenDropdown
          onRegenPost={onRegeneratePost}
          onRegenReel={onGenerateReel}
          className={outlineBtn}
        />
      </div>
    );
  }

  if (cardState === 'published' && postId) {
    if (hasGeneratedReel) {
      return (
        <div className="flex items-center gap-2">
          <Button onClick={onViewPost} size="sm" className="gap-1.5">
            <FileText className="h-3.5 w-3.5" />
            View Post
          </Button>
          <button onClick={onViewReel} className={outlineBtn}>
            <Film className="h-3.5 w-3.5" />
            View Reel
          </button>
          <PublishButtonMemo postId={postId} className="h-8 text-xs" buttonText="Republish" />
        </div>
      );
    }
    return (
      <div className="flex items-center gap-2">
        <Button onClick={onViewPost} size="sm" className="gap-1.5">
          <FileText className="h-3.5 w-3.5" />
          View Post
        </Button>
        <PublishButtonMemo postId={postId} className="h-8 text-xs" buttonText="Republish" />
        <RegenDropdown
          onRegenPost={onRegeneratePost}
          onRegenReel={onGenerateReel}
          className={outlineBtn}
        />
      </div>
    );
  }

  return <div className="flex items-center gap-2" />;
}

/** Right side of action bar: secondary actions + edit/delete */
function TopicActionBarRight({
  cardState,
  onGenerateReel,
  onViewReel,
  onGenerate,
  onRegeneratePost,
  hasGeneratedReel,
  isReelGenerating,
  onDelete,
  onEdit,
  topicSource,
}: {
  cardState: string;
  onGenerateReel: () => void;
  onViewReel: () => void;
  onGenerate: () => void;
  onRegeneratePost: () => void;
  hasGeneratedReel: boolean;
  isReelGenerating: boolean;
  onDelete: () => void;
  onEdit?: () => void;
  topicSource: string;
}) {
  const reelBtnClass =
    'bg-topic-btn-reel-bg text-topic-btn-reel border border-topic-btn-reel-border hover:opacity-80 text-xs font-medium px-3.5 py-[7px] rounded-lg gap-1.5 inline-flex items-center transition-opacity';
  const postBtnClass =
    'bg-topic-btn-post-bg text-topic-btn-post border border-topic-btn-post-border hover:opacity-80 text-xs font-medium px-3.5 py-[7px] rounded-lg gap-1.5 inline-flex items-center transition-opacity';
  const outlineBtn =
    'border border-icon-btn-border text-topic-action-text hover:text-foreground text-xs font-medium px-3.5 py-[7px] rounded-lg gap-1.5 inline-flex items-center transition-colors';
  const iconBtn =
    'flex items-center justify-center h-8 w-8 rounded-lg border border-icon-btn-border text-icon-btn-fg transition-colors';

  const renderSecondaryAction = () => {
    const postRelatedStates = ['post_generated', 'generating_image', 'image_generated'];
    if (postRelatedStates.includes(cardState)) {
      if (isReelGenerating) {
        return (
          <Button variant="outline" size="sm" disabled className="gap-2">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Reel...
          </Button>
        );
      }
      if (hasGeneratedReel) {
        return (
          <button onClick={onViewReel} className={outlineBtn}>
            <Film className="h-3.5 w-3.5" />
            View Reel
          </button>
        );
      }
      return (
        <button onClick={onGenerateReel} className={reelBtnClass}>
          <Film className="h-3.5 w-3.5" />
          Generate Reel
        </button>
      );
    }

    if (cardState === 'published' && !hasGeneratedReel) {
      if (isReelGenerating) {
        return (
          <Button variant="outline" size="sm" disabled className="gap-2">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Reel...
          </Button>
        );
      }
      return (
        <button onClick={onGenerateReel} className={reelBtnClass}>
          <Film className="h-3.5 w-3.5" />
          Generate Reel
        </button>
      );
    }

    if (cardState === 'published' && hasGeneratedReel) {
      return (
        <RegenDropdown
          onRegenPost={onRegeneratePost}
          onRegenReel={onGenerateReel}
          className={outlineBtn}
        />
      );
    }

    if (cardState === 'reel_generated') {
      return (
        <button onClick={onGenerate} className={postBtnClass}>
          <FileText className="h-3.5 w-3.5" />
          Generate Post
        </button>
      );
    }

    return null;
  };

  return (
    <div className="flex items-center gap-2">
      {renderSecondaryAction()}
      {topicSource === 'manual' && onEdit && (
        <button
          onClick={onEdit}
          className={cn(iconBtn, 'hover:text-foreground')}
          aria-label="Edit topic"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
      )}
      <button
        onClick={onDelete}
        className={cn(iconBtn, 'hover:text-destructive')}
        aria-label="Delete topic"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

/** Dropdown with options to regenerate post or reel */
function RegenDropdown({
  onRegenPost,
  onRegenReel,
  className,
}: {
  onRegenPost: () => void;
  onRegenReel: () => void;
  className?: string;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className={className}>
          <Sparkles className="h-3.5 w-3.5" />
          Regen
          <ChevronDown className="h-3 w-3 opacity-60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={onRegenPost}>
          <FileText className="h-3.5 w-3.5 mr-2" />
          Regen Post
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onRegenReel}>
          <Film className="h-3.5 w-3.5 mr-2" />
          Regen Reel
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export { TopicCard };
